import { useState } from 'react'

import { create } from '@bufbuild/protobuf'
import type { Timestamp } from '@bufbuild/protobuf/wkt'
import { Code, ConnectError } from '@connectrpc/connect'
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { DeleteWorkflowCaseRequestSchema } from '@ebalance/proto/bffuser/v1/workflow_case_service_pb'
import type {
  WorkflowCaseTag,
  WorkflowSection,
} from '@ebalance/proto/bffuser/v1/workflow_template_model_pb'
import type { Date as ProtoDate } from '@ebalance/proto/google/type/date_pb'
import { useTranslation } from 'react-i18next'

import {
  Alert,
  Box,
  DangerButton,
  HStack,
  TertiaryButton,
  Text,
  VStack,
  chakra,
} from '@enechain/ecloud-designsystem'

import { Modal } from '~/components/common/Modal'
import { WorkflowCaseTagList } from '~/components/common/WorkflowCaseTagList'
import { useErrorToast } from '~/hooks/useErrorToast'
import { useSuccessToast } from '~/hooks/useSuccessToast'
import {
  useDeleteWorkflowCase,
  useReloadAfterDeleteWorkflowCase,
} from '~/hooks/workflowCase/useDeleteWorkflowCase'
import { formatDateDisplay, fromProtoDate } from '~/models/dateTime'
import { areaLabelOrDash } from '~/models/workflowCase'
import { workflowSectionLabelKey } from '~/models/workflowSection'
import { WORKFLOW_CASE_BG_ABSENT_LABEL } from '~/pages/WorkflowCases/models/workflowCase'

/**
 * 削除ダイアログが参照する最小限の識別情報。 一覧行 (`WorkflowCaseRow`) と詳細画面の
 * `WorkflowCase` proto の両方が構造的にこの形を満たすため、 呼び出し元 (WorkflowCases
 * 一覧 / WorkflowCaseDetail 詳細) の両方から同一コンポーネントを再利用できる
 * (= 複数ページで再利用するため `components/common/DeleteCaseDialog/` に配置する。
 * page 配下 components はページ内専用のため、cross-page 再利用は components/common
 * という frontend CLAUDE.md 規約に従う)。
 */
export type DeletableCase = {
  id: string
  /** human-readable business key (spec AC-WFW-003f の削除対象情報「ID」)。 */
  workflowId: string
  tags: WorkflowCaseTag[]
  section: WorkflowSection
  areaCode: AreaCode
  /** 未設定 = 「BG なし」 (workflow-bg-code-derivation design)。表示は「—」。 */
  bgCode?: string | undefined
  targetDate?: ProtoDate | undefined
  updatedAt?: Timestamp | undefined
}

type Props = {
  /** 削除対象の行。 undefined の場合はダイアログ非表示。 */
  row: DeletableCase | undefined
  onClose: () => void
}

/**
 * ワークフロー削除確認ダイアログ (spec AC-WFW-003f/g)。
 *
 * 見出し + 不可逆警告 alert + 削除対象情報 (ID / 区分(タグ) / 計画断面 / エリア /
 * BG コード / 対象日) の一覧表示 + キャンセル/削除ボタンの 4 要素のみで構成する
 * (workflow-delete-dialog-prototype-simplification design)。削除理由の選択・補足入力・
 * 広域機関提出警告は spec AC-WFW-003f の除外注記により本ダイアログに含めない
 * (prototype `design/demand/case-detail.html` は未追随 = Design gap)。
 * 未着手・進行中でない行は呼び出し側 (CaseTable の削除ボタン非表示 / CaseActionBar の
 * 削除ボタン非表示) で本ダイアログを開けないようにする (AC-01)。
 */
export const DeleteCaseDialog = ({
  row,
  onClose,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const showSuccessToast = useSuccessToast()
  const showErrorToast = useErrorToast()
  // 詳細画面 (WorkflowCaseDetail) の cache も invalidate するため row.id を渡す
  // (row undefined 時は list query のみ)。
  const { mutateAsync: deleteAsync, isPending } = useDeleteWorkflowCase(row?.id)
  const reloadAffectedQueries = useReloadAfterDeleteWorkflowCase(row?.id)
  // `reloadable` は先に他の更新が入っていた時で、 エラーの横に「再読み込み」 を置く
  // (AC-WFW-003s)。
  const [error, setError] = useState<
    { message: string; reloadable: boolean } | undefined
  >(undefined)

  const handleClose = (): void => {
    setError(undefined)
    onClose()
  }

  const handleReload = (): void => {
    handleClose()
    void reloadAffectedQueries()
  }

  const handleDelete = async (): Promise<void> => {
    if (row === undefined) return
    setError(undefined)
    try {
      await deleteAsync(
        create(DeleteWorkflowCaseRequestSchema, {
          id: row.id,
          expectedUpdatedAt: row.updatedAt,
        }),
      )
      handleClose()
      // 削除成功の toast (2026-08-28 デザイナー指摘「削除・追加・編集の成功時は
      // 全画面共通で toast を出す」)。 本 dialog は一覧と詳細の両方から使われる
      // ので、 1 箇所で 2 画面ぶん満たされる。
      showSuccessToast({ title: t('page:workflowCases.delete.successToast') })
    } catch (err) {
      if (err instanceof ConnectError) {
        switch (err.code) {
          case Code.Aborted:
            setError({
              message: t('page:workflowCases.serverError.aborted'),
              reloadable: true,
            })
            break
          case Code.FailedPrecondition:
            handleClose()
            showErrorToast({
              title: t(
                'page:workflowCases.serverError.alreadyCompletedOrDeleted',
              ),
            })
            void reloadAffectedQueries()
            break
          default:
            setError({
              message: t('page:workflowCases.serverError.deleteFailed'),
              reloadable: false,
            })
        }
      } else {
        setError({
          message: t('page:workflowCases.serverError.deleteFailed'),
          reloadable: false,
        })
      }
    }
  }

  const footer = (
    <HStack gap={3}>
      <TertiaryButton disabled={isPending} onClick={handleClose}>
        {t('common:action.cancel')}
      </TertiaryButton>
      <DangerButton
        loading={isPending}
        onClick={() => {
          void handleDelete()
        }}
      >
        {t('page:workflowCases.delete.confirmLabel')}
      </DangerButton>
    </HStack>
  )

  return (
    <Modal
      footer={footer}
      isOpen={row !== undefined}
      onClose={handleClose}
      size="md"
      title={t('page:workflowCases.delete.title')}
    >
      <VStack align="stretch" gap={4}>
        {error !== undefined && (
          <Alert status="error" title={error.message}>
            {error.reloadable && (
              <TertiaryButton onClick={handleReload} size="sm">
                {t('page:workflowCases.delete.reload')}
              </TertiaryButton>
            )}
          </Alert>
        )}
        {/* AC-WFW-003f。 本文の「次の」 が直下の削除対象情報を指すよう、 警告の後に置く
            (design `demand/cases.html` の削除ダイアログと同じ並び)。 */}
        <Alert
          description={t('page:workflowCases.delete.warningDescription')}
          status="warning"
          title={t('page:workflowCases.delete.warningTitle')}
        />
        <Text color="Text.highEmphasis" fontSize="md">
          {t('page:workflowCases.delete.body')}
        </Text>

        {/* AC-WFW-003f: 削除対象情報 (ID / 区分(タグ) / 計画断面 / エリア / BG コード /
            対象日) の一覧表示。ラベルは CaseTable の列見出しと同じ i18n key を再利用する
            (spec AC-WFW-001 の 13 列と同一語彙)。 */}
        {row !== undefined && (
          <Box
            padding="12px 16px"
            background="Surface.secondary"
            borderRadius="base"
          >
            <chakra.dl
              flexWrap="wrap"
              gap="16px"
              display="flex"
              margin={0}
              padding={0}
            >
              <DeleteTargetMetaItem label={t('page:workflowCases.column.id')}>
                <Text
                  color="Text.highEmphasis"
                  fontSize="md"
                  fontVariantNumeric="tabular-nums"
                >
                  {row.workflowId}
                </Text>
              </DeleteTargetMetaItem>
              <DeleteTargetMetaItem label={t('page:workflowCases.column.tags')}>
                <WorkflowCaseTagList tags={row.tags} />
              </DeleteTargetMetaItem>
              <DeleteTargetMetaItem
                label={t('page:workflowCases.column.section')}
              >
                <Text color="Text.highEmphasis" fontSize="md">
                  {t(
                    workflowSectionLabelKey('workflowCases', row.section) ?? '',
                  )}
                </Text>
              </DeleteTargetMetaItem>
              <DeleteTargetMetaItem label={t('page:workflowCases.column.area')}>
                <Text color="Text.highEmphasis" fontSize="md">
                  {/* issue #5219: BG 不要ケースは area_code が UNSPECIFIED で
                      返る。areaLabelOrDash が空欄でなく「—」に落とす。 */}
                  {areaLabelOrDash(row.areaCode)}
                </Text>
              </DeleteTargetMetaItem>
              {/* BG だけ一覧と key が違う: 一覧の見出しは「BG コード・名称」
                  (`column.bgCodeName`) だが、 このダイアログは bgCode しか描かないので
                  「名称」 を含む文言を流用できない (issue #5748 / PR #5750 の判断)。 */}
              <DeleteTargetMetaItem label={t('page:workflowCases.column.bg')}>
                <Text color="Text.highEmphasis" fontSize="md">
                  {/* AC-11: BG 軸を持たないケースは空欄でなく「—」を出す。 */}
                  {row.bgCode ?? WORKFLOW_CASE_BG_ABSENT_LABEL}
                </Text>
              </DeleteTargetMetaItem>
              <DeleteTargetMetaItem
                label={t('page:workflowCases.column.targetDate')}
              >
                <Text color="Text.highEmphasis" fontSize="md">
                  {row.targetDate !== undefined
                    ? formatDateDisplay(fromProtoDate(row.targetDate))
                    : '-'}
                </Text>
              </DeleteTargetMetaItem>
            </chakra.dl>
          </Box>
        )}
      </VStack>
    </Modal>
  )
}

const DeleteTargetMetaItem = ({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}): React.ReactElement => (
  <Box flexDirection="column" gap="2px" display="flex">
    <Text as="dt" textStyle="label" margin={0} color="Text.highEmphasis">
      {label}
    </Text>
    <Box as="dd" margin={0}>
      {children}
    </Box>
  </Box>
)
